import React from 'react';
import {
  View,
  Text,
  Image,
  Pressable,
  StyleSheet,
  ScrollView,
  Platform,
} from 'react-native';
import { FontAwesome6, FontAwesome5, FontAwesome } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { ChargerAttributes, isChargerOffline } from '../models/charger-model';
import LoadingPlaceholder from './LoadingPlaceholder';
import { getPriceInfo } from './common/utils/get-price-info';
import { UserPlanAssociation } from '../models/user-plan-associations-model';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import ChargerImageLoader from './ChargerImageLoader';

interface TimeAndDistance {
  chargerId: string;
  myDistance: string;
  myTime: string;
}

const ChargerSelectMultiplePopup = (props: {
  selectedChargers: ChargerAttributes[];
  myTimeAndDistance: TimeAndDistance[];
  handleViewCharger: (charger: ChargerAttributes, index: number) => void;
}) => {
  const driverPlan: UserPlanAssociation | null = useSelector(
    (state: RootState) => state.user.driverPlan
  );

  return (
    <View
      style={
        Platform.OS === 'web'
          ? {
              maxWidth: 430,
              width: '100%',
              marginLeft: 'auto',
              marginRight: 'auto',
            }
          : { width: '100%' }
      }
    >
      {/* Header / Multiple Chargers View */}
      <View style={styles.headerContainer}>
        <Image
          source={require('../../assets/headerBarIcon.png')}
          style={styles.headerIcon}
        />
      </View>

      {/* Content */}
      <View style={styles.contentContainer}>
        <ScrollView style={styles.scrollView}>
          {props.selectedChargers.map((item, index) => {
            // getPriceInfo is assumed to return { currentPrice, priceLabel, nextTransition }
            const { currentPrice, priceLabel, nextTransition, todayPeak, unit } =
              getPriceInfo(item, driverPlan);

            return (
              <View key={item.uuid}>
                <View style={styles.row}>
                  {/* Left Icon */}
                  <View>
                    <Image
                      source={require('../../assets/stationPlugIn.png')}
                      style={[
                        styles.stationPlug,
                        {
                          tintColor: isChargerOffline(item) ? '#6B7280' : item.isOccupied === 1 ? 'red' : undefined,
                        },
                      ]}
                    />
                  </View>

                  {/* Charger Type Icon */}
                  <View>
                    <Image
                      source={require('../../assets/chargerType.png')}
                      style={styles.chargerType}
                    />
                  </View>

                  {/* Charger Details */}
                  <View style={styles.detailsContainer}>
                    <Text style={[styles.chargerName, styles.textColor]}>
                      {item.name}
                    </Text>
                    <Text style={styles.priceText}>
                      <FontAwesome name="dollar" size={14} color="#fff" />
                      {` ${currentPrice.toFixed(3)}${unit}`}
                      {todayPeak && todayPeak.start !== todayPeak.end && (
                        <Text
                          style={styles.extraInfo}
                        >{` (${priceLabel})`}</Text>
                      )}
                      {nextTransition && (
                        <Text
                          style={styles.extraInfo}
                        >{`\n[${nextTransition}]`}</Text>
                      )}
                    </Text>
                    <View style={styles.infoRow}>
                      <FontAwesome6
                        name="location-dot"
                        size={17}
                        color="#8D8E90"
                      />
                      <Text style={styles.infoText}>
                        {props.myTimeAndDistance[index]?.myDistance !==
                        undefined ? (
                          `${props.myTimeAndDistance[index].myDistance} mi`
                        ) : (
                          <LoadingPlaceholder />
                        )}
                      </Text>
                      <FontAwesome5
                        name="car"
                        size={17}
                        color="#8D8E90"
                        style={styles.carIcon}
                      />
                      <Text style={styles.infoText}>
                        {props.myTimeAndDistance[index]?.myTime !==
                        undefined ? (
                          `${props.myTimeAndDistance[index].myTime}`
                        ) : (
                          <LoadingPlaceholder />
                        )}
                      </Text>
                    </View>
                  </View>

                  {/* View Charger Button */}
                  <View>
                    <Pressable
                      style={styles.btn2}
                      onPress={async () => { try { await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); } catch {} props.handleViewCharger(item, index); }}
                    >
                      <Text style={styles.btn2Text}>View Charger</Text>
                    </Pressable>
                  </View>
                </View>

                {/* Separator Line */}
                {index < props.selectedChargers.length - 1 && (
                  <View style={styles.separator} />
                )}
              </View>
            );
          })}
        </ScrollView>
      </View>
    </View>
  );
};

export default ChargerSelectMultiplePopup;

const styles = StyleSheet.create({
  headerContainer: {
    alignItems: 'center',
    marginBottom: 16,
    marginTop: 8,
  },
  headerIcon: {
    width: 55,
    height: 5,
    marginTop: 10,
  },
  contentContainer: {
    padding: 16,
  },
  scrollView: {
    maxHeight: 400,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  stationPlug: {
    width: 30,
    height: 38,
    marginLeft: 6,
    marginRight: 4,
  },
  chargerType: {
    width: 40,
    height: 40,
  },
  detailsContainer: {
    flex: 1,
    marginHorizontal: 8,
    marginRight: 12,
  },
  chargerName: {
    fontSize: 12,
  },
  priceText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
    marginTop: 4,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
  },
  infoText: {
    color: '#8D8E90',
    marginLeft: 4,
  },
  carIcon: {
    marginLeft: 12,
  },
  separator: {
    borderBottomColor: '#636363',
    borderBottomWidth: 1,
    borderStyle: 'dashed',
    marginVertical: 12,
  },
  btn1: {
    borderColor: '#8D8E90',
    borderWidth: 2,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 30,
    backgroundColor: 'transparent',
  },
  btn2: {
    backgroundColor: '#39BEB7',
    borderRadius: 20,
    paddingHorizontal: 6,
    paddingVertical: 8,
  },
  btn2Text: {
    fontWeight: '500',
    color: 'white',
    fontSize: 12,
  },
  textColor: {
    color: '#E8E8E9',
  },
  extraInfo: {
    fontSize: 12,
    color: '#39BEB7',
  },
});
